import React, { useId, useState, type ReactNode } from 'react';
import {
  View,
  Text,
  Pressable,
  TextInput,
  ScrollView,
  Modal,
  KeyboardAvoidingView,
  Platform,
  ActivityIndicator,
  useWindowDimensions,
  type ViewStyle,
  type TextStyle,
} from 'react-native';
import { type Profile } from '@ventureroom/core';
import { useApp, useTheme, type Destination } from './provider';
import { LogoInput } from './logo-input';
export function Copy({
  children,
  size = 15,
  muted = false,
  bold = false,
  color,
  style,
  lines,
}: {
  children: ReactNode;
  size?: number;
  muted?: boolean;
  bold?: boolean;
  color?: string;
  style?: TextStyle;
  lines?: number;
}) {
  const t = useTheme();
  return (
    <Text
      numberOfLines={lines}
      style={[
        {
          fontSize: size,
          lineHeight: Math.round(size * 1.5),
          color: color ?? (muted ? t.muted : t.text),
          fontWeight: bold ? '600' : '400',
          flexShrink: 1,
          minWidth: 0,
          ...(Platform.OS === 'web' ? ({ overflowWrap: 'anywhere' } as TextStyle) : {}),
          fontFamily:
            Platform.OS === 'web' ? 'Inter, ui-sans-serif, system-ui, sans-serif' : undefined,
        },
        style,
      ]}
    >
      {children}
    </Text>
  );
}
export function Heading({ children, size = 28 }: { children: ReactNode; size?: number }) {
  const t = useTheme();
  return (
    <Text
      accessibilityRole="header"
      style={{
        fontSize: size,
        lineHeight: size * 1.16,
        fontWeight: '700',
        letterSpacing: -size * 0.035,
        color: t.text,
        flexShrink: 1,
        minWidth: 0,
      }}
    >
      {children}
    </Text>
  );
}
export function Row({
  children,
  wrap = false,
  between = false,
  style,
}: {
  children: ReactNode;
  wrap?: boolean;
  between?: boolean;
  style?: ViewStyle;
}) {
  return (
    <View
      style={[
        {
          flexDirection: 'row',
          minWidth: 0,
          maxWidth: '100%',
          alignItems: 'center',
          gap: 10,
          flexWrap: wrap ? 'wrap' : 'nowrap',
          justifyContent: between ? 'space-between' : undefined,
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}
export function Column({
  children,
  gap = 16,
  style,
}: {
  children: ReactNode;
  gap?: number;
  style?: ViewStyle;
}) {
  return <View style={[{ gap }, style]}>{children}</View>;
}
export function Card({
  children,
  style,
  onPress,
  label,
}: {
  children: ReactNode;
  style?: ViewStyle;
  onPress?: () => void;
  label?: string;
}) {
  const t = useTheme();
  const { width } = useWindowDimensions();
  const fit =
    width < 600 && style?.minWidth
      ? {
          minWidth: 0,
          width: '100%' as const,
          flexBasis: '100%' as const,
          maxWidth: '100%' as const,
        }
      : { maxWidth: '100%' as const };
  const base: ViewStyle = {
    backgroundColor: 'transparent',
    borderBottomWidth: 1,
    borderColor: t.border,
    paddingVertical: 20,
    gap: 12,
  };
  return onPress ? (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={label}
      onPress={onPress}
      style={({ pressed }) => [base, { opacity: pressed ? 0.82 : 1 }, style, fit]}
    >
      {children}
    </Pressable>
  ) : (
    <View style={[base, style, fit]}>{children}</View>
  );
}
export function Button({
  children,
  onPress,
  kind = 'primary',
  disabled = false,
  compact = false,
  label,
  color,
  feedback = true,
}: {
  children: ReactNode;
  onPress: () => void;
  kind?: 'primary' | 'secondary' | 'quiet' | 'danger';
  disabled?: boolean;
  compact?: boolean;
  label?: string;
  color?: string;
  feedback?: boolean;
}) {
  const t = useTheme();
  const app = useApp();
  const actionId = useId();
  const result = app.actionFeedback.id === actionId ? app.actionFeedback : null;
  return (
    <View style={{ maxWidth: '100%', flexShrink: 1, gap: 6 }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        accessibilityState={{ disabled }}
        disabled={disabled}
        onPress={() => {
          app.beginAction(actionId);
          onPress();
        }}
        style={({ pressed }) => ({
          minHeight: 44,
          maxWidth: '100%',
          flexShrink: 1,
          paddingHorizontal: compact ? 14 : 22,
          paddingVertical: 11,
          borderRadius: 14,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor:
            kind === 'primary'
              ? color || t.accent
              : kind === 'secondary'
                ? 'transparent'
                : kind === 'danger'
                  ? 'transparent'
                  : 'transparent',
          borderWidth: 0,
          borderColor: t.border,
          opacity: disabled ? 0.45 : pressed ? 0.75 : 1,
        })}
      >
        <Text
          style={{
            fontWeight: '600',
            fontSize: 14,
            color:
              color && kind !== 'primary'
                ? color
                : kind === 'primary'
                  ? t.onAccent
                  : kind === 'danger'
                    ? t.danger
                    : t.text,
          }}
        >
          {children}
        </Text>
      </Pressable>
      {feedback && !!result?.error && (
        <View
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
          style={{
            padding: 8,
            borderRadius: 8,
            borderWidth: 1,
            borderColor: t.danger,
            backgroundColor: t.surface,
          }}
        >
          <Copy size={12} color={t.danger}>
            {result.error.startsWith('[')
              ? 'This action failed. Check the fields and try again.'
              : result.error}
          </Copy>
        </View>
      )}
      {feedback && !result?.error && !!result?.success && (
        <Copy size={12} muted>
          {result.success}
        </Copy>
      )}
    </View>
  );
}
export function Tag({ children, accent = false }: { children: ReactNode; accent?: boolean }) {
  const t = useTheme();
  return (
    <View
      style={{
        paddingVertical: 2,
        alignSelf: 'flex-start',
      }}
    >
      <Copy size={11} color={accent ? t.accent : t.muted} bold>
        {children}
      </Copy>
    </View>
  );
}
export function Avatar({
  person,
  size = 56,
}: {
  person: Pick<Profile, 'name' | 'avatar'>;
  size?: number;
}) {
  const t = useTheme();
  return (
    <View
      accessibilityLabel={`${person.name} avatar`}
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.34,
        backgroundColor: t.avatars[person.avatar],
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <View
        style={{
          position: 'absolute',
          width: size * 0.8,
          height: size * 0.8,
          borderRadius: 100,
          borderWidth: size * 0.15,
          borderColor: t.surface,
          opacity: 0.2,
          right: -size * 0.3,
          bottom: -size * 0.3,
        }}
      />
      <Text style={{ fontSize: size * 0.33, fontWeight: '600', color: t.text }}>
        {person.name
          .split(' ')
          .map((s) => s[0])
          .slice(0, 2)
          .join('')}
      </Text>
    </View>
  );
}
export function Brand({ small = false }: { small?: boolean }) {
  const t = useTheme();
  return (
    <Row>
      <View
        style={{
          width: small ? 30 : 38,
          height: small ? 30 : 38,
          borderRadius: 11,
          backgroundColor: t.accent,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ color: t.onAccent, fontWeight: '800', fontSize: 23 }}>v.</Text>
      </View>
      <Text
        style={{ fontSize: small ? 20 : 24, fontWeight: '700', letterSpacing: -0.8, color: t.text }}
      >
        VentureRoom
      </Text>
    </Row>
  );
}
export function Empty({
  title,
  detail,
  action,
}: {
  title: string;
  detail: string;
  action?: ReactNode;
}) {
  const t = useTheme();
  return (
    <Card style={{ paddingVertical: 40, alignItems: 'center' }}>
      <View
        style={{
          height: 64,
          width: 64,
          borderRadius: 24,
          backgroundColor: t.hero,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Copy size={28}>↗</Copy>
      </View>
      <Heading size={20}>{title}</Heading>
      <Copy muted style={{ maxWidth: 380 }}>
        {detail}
      </Copy>
      {action}
    </Card>
  );
}
export function Notice() {
  return null;
}
export function Page({
  title,
  action,
  back,
  scroll = true,
  children,
}: {
  title: string;
  action?: ReactNode;
  back?: Destination | (() => void);
  scroll?: boolean;
  children: ReactNode;
}) {
  const t = useTheme();
  const { goBack } = useApp();
  const { width, height } = useWindowDimensions();
  const padding = width < 600 ? 24 : 64;
  const bodyStyle: ViewStyle = {
    paddingHorizontal: padding,
    paddingTop: 8,
    paddingBottom: scroll ? 36 : 12,
    width: '100%',
    maxWidth: 1250,
    alignSelf: 'center',
    gap: scroll ? 20 : 12,
    ...(!scroll ? { flex: 1, minHeight: 0 } : {}),
  };
  return (
    <View
      style={{
        flex: 1,
        minHeight: 0,
        backgroundColor: t.background,
        ...(!scroll && Platform.OS === 'web' ? { height: Math.max(590, height - 140) } : {}),
      }}
    >
      {!!(title || back || action) && (
        <View
          style={{
            paddingHorizontal: padding,
            paddingTop: 24,
            paddingBottom: 16,
            width: '100%',
            maxWidth: 1250,
            alignSelf: 'center',
          }}
        >
          <Row between>
            {back && (
              <Button
                compact
                kind="quiet"
                label="Back"
                onPress={() => (typeof back === 'function' ? back() : goBack(back))}
              >
                ←
              </Button>
            )}
            <View style={{ flex: 1 }}>
              <Heading size={width < 600 ? 25 : 30}>{title}</Heading>
            </View>
            {action}
          </Row>
        </View>
      )}
      {scroll ? (
        <ScrollView
          style={{ flex: 1, minHeight: 0 }}
          contentContainerStyle={bodyStyle}
          keyboardShouldPersistTaps="handled"
        >
          <Notice />
          {children}
        </ScrollView>
      ) : (
        <View style={bodyStyle}>
          <Notice />
          {children}
        </View>
      )}
    </View>
  );
}
export function Loading() {
  const t = useTheme();
  return (
    <View
      style={{
        flex: 1,
        backgroundColor: t.background,
        padding: 40,
        justifyContent: 'center',
        gap: 22,
      }}
    >
      <Brand />
      <ActivityIndicator color={t.accent} />
      <Copy muted>Opening your room…</Copy>
      {[1, 2, 3].map((i) => (
        <View key={i} style={{ height: 70, borderRadius: 20, backgroundColor: t.surfaceAlt }} />
      ))}
    </View>
  );
}
export function Segments({
  items,
  value,
  onChange,
}: {
  items: string[];
  value: string;
  onChange: (s: string) => void;
}) {
  const t = useTheme();
  const app = useApp();
  const actionId = useId();
  const result = app.actionFeedback.id === actionId ? app.actionFeedback : undefined;
  return (
    <Column gap={6}>
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          maxWidth: '100%',
          gap: 6,
          paddingVertical: 2,
        }}
      >
        {items.map((item) => (
          <Pressable
            key={item}
            accessibilityRole="tab"
            accessibilityState={{ selected: value === item }}
            aria-selected={value === item}
            onPress={() => {
              app.beginAction(actionId);
              onChange(item);
            }}
            style={{
              paddingHorizontal: 18,
              paddingVertical: 12,
              minHeight: 44,
              maxWidth: '100%',
              borderBottomWidth: 2,
              borderBottomColor: value === item ? t.accent : 'transparent',
            }}
          >
            <Copy size={13} bold color={value === item ? t.text : t.muted}>
              {item}
            </Copy>
          </Pressable>
        ))}
      </View>
      {!!result?.error && (
        <View
          accessibilityRole="alert"
          style={{ padding: 8, borderWidth: 1, borderColor: t.danger, borderRadius: 8 }}
        >
          <Copy size={12} color={t.danger}>
            {result.error}
          </Copy>
        </View>
      )}
      {!!result?.success && !result.error && (
        <Copy muted size={12}>
          {result.success}
        </Copy>
      )}
    </Column>
  );
}
export function Metric({ value, label }: { value: string | number; label: string }) {
  return (
    <Column gap={3} style={{ flex: 1, minWidth: 95 }}>
      <Heading size={27}>{value}</Heading>
      <Copy size={12} muted>
        {label}
      </Copy>
    </Column>
  );
}
export type Field = {
  key: string;
  label: string;
  type?: 'text' | 'long' | 'number' | 'select' | 'multi' | 'secret' | 'logo';
  options?: string[];
  hint?: string;
  required?: boolean;
  allowOther?: boolean;
};
export type FormValues = Record<string, string | string[] | number>;
export function Input({
  label,
  value,
  onChange,
  onSubmit,
  placeholder,
  multiline = false,
  secret = false,
  numeric = false,
  email = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onSubmit?: () => void;
  placeholder?: string;
  multiline?: boolean;
  secret?: boolean;
  numeric?: boolean;
  email?: boolean;
}) {
  const t = useTheme();
  return (
    <Column gap={7}>
      <Copy size={13} bold>
        {label}
      </Copy>
      <TextInput
        accessibilityLabel={label}
        value={value}
        onChangeText={onChange}
        onSubmitEditing={onSubmit}
        onKeyPress={(e) => {
          if (e.nativeEvent.key === 'Enter' && onSubmit) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if ((e.nativeEvent as any).shiftKey) return;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            if (typeof (e as any).preventDefault === 'function') {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (e as any).preventDefault();
            }
            onSubmit();
          }
        }}
        placeholder={placeholder}
        placeholderTextColor={t.muted}
        multiline={multiline}
        secureTextEntry={secret}
        keyboardType={numeric ? 'decimal-pad' : email ? 'email-address' : 'default'}
        autoCapitalize={secret || email ? 'none' : 'sentences'}
        autoCorrect={!secret && !email}
        style={{
          borderWidth: 1,
          borderColor: t.border,
          borderRadius: 12,
          padding: 13,
          fontSize: 15,
          color: t.text,
          backgroundColor: t.surface,
          minHeight: multiline ? 90 : 48,
          textAlignVertical: multiline ? 'top' : 'center',
        }}
      />
    </Column>
  );
}
export function MultiChoice({
  label,
  options,
  value,
  onChange,
  allowOther = false,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (value: string[]) => void;
  allowOther?: boolean;
}) {
  const t = useTheme();
  const [other, setOther] = useState(false),
    [draft, setDraft] = useState('');
  const [expanded, setExpanded] = useState(false);
  const custom = value.filter((item) => !options.includes(item));
  const displayed = expanded
    ? options
    : options.filter((item, index) => index < 10 || value.includes(item));
  const add = () => {
    const entry = draft.trim();
    if (!entry || entry.length > 60 || value.length >= 20) return;
    if (!value.some((v) => v.toLowerCase() === entry.toLowerCase())) onChange([...value, entry]);
    setDraft('');
  };
  return (
    <Column gap={8}>
      <Copy size={13} bold>
        {label}
      </Copy>
      <Row wrap>
        {[...displayed, ...custom].map((option) => {
          const selected = value.includes(option);
          return (
            <Pressable
              key={option}
              accessibilityRole="checkbox"
              accessibilityLabel={`${label}: ${option}`}
              accessibilityState={{ checked: selected }}
              aria-checked={selected}
              onPress={() =>
                onChange(
                  selected
                    ? value.filter((v) => v !== option)
                    : value.length < 20
                      ? [...value, option]
                      : value,
                )
              }
              style={{
                paddingVertical: 11,
                paddingHorizontal: 13,
                borderRadius: 11,
                borderWidth: 1,
                borderColor: selected ? t.accent : t.border,
                backgroundColor: selected ? t.accentSoft : t.surface,
                minHeight: 44,
                maxWidth: '100%',
              }}
            >
              <Copy size={13} color={selected ? t.accent : t.muted}>
                {selected ? '✓  ' : ''}
                {option}
              </Copy>
            </Pressable>
          );
        })}
        {allowOther && (
          <Button kind="secondary" onPress={() => setOther(!other)} label={`${label}: Other`}>
            Other…
          </Button>
        )}
      </Row>
      {options.length > 10 && (
        <Row>
          <Button
            kind="quiet"
            label={`${label}: ${expanded ? 'Show fewer choices' : 'Show all choices'}`}
            onPress={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show fewer' : 'More choices'}
          </Button>
        </Row>
      )}
      {other && (
        <Column gap={8}>
          <Input
            label={`${label} — other`}
            value={draft}
            onChange={setDraft}
            placeholder="Add your own skill or resource"
            onSubmit={add}
          />
          <Row>
            <Button
              kind="quiet"
              disabled={!draft.trim() || draft.trim().length > 60 || value.length >= 20}
              onPress={add}
            >
              Add selection
            </Button>
          </Row>
          <Copy muted size={12}>
            Up to 20 selections; 60 characters per custom entry.
          </Copy>
        </Column>
      )}
    </Column>
  );
}
function CustomSelect({
  field,
  value,
  onChange,
}: {
  field: Field;
  value: string;
  onChange: (value: string) => void;
}) {
  const [other, setOther] = useState(!!value && !field.options?.includes(value));
  const t = useTheme();
  return (
    <Column gap={8}>
      <Copy size={13} bold>
        {field.label}
      </Copy>
      <Row wrap>
        {field.options?.map((option) => (
          <Pressable
            key={option}
            accessibilityRole="radio"
            aria-checked={!other && value === option}
            accessibilityState={{ checked: !other && value === option }}
            accessibilityLabel={`${field.label}: ${option}`}
            onPress={() => {
              setOther(false);
              onChange(option);
            }}
            style={{
              paddingVertical: 11,
              paddingHorizontal: 13,
              borderRadius: 11,
              borderWidth: 1,
              borderColor: !other && value === option ? t.accent : t.border,
              minHeight: 44,
            }}
          >
            <Copy size={13}>
              {!other && value === option ? '✓  ' : ''}
              {option}
            </Copy>
          </Pressable>
        ))}
        <Button
          kind="secondary"
          label={`${field.label}: Other`}
          onPress={() => {
            if (!other) onChange('');
            setOther(true);
          }}
        >
          Other…
        </Button>
      </Row>
      {other && (
        <Input
          label={`${field.label} — other`}
          value={value}
          onChange={onChange}
          placeholder="Enter your industry"
        />
      )}
      {!!value && !field.required && (
        <Row>
          <Button
            kind="quiet"
            label={`Clear ${field.label.toLowerCase()}`}
            onPress={() => {
              onChange('');
              setOther(false);
            }}
          >
            Clear selection
          </Button>
        </Row>
      )}
    </Column>
  );
}
export function FormFields({
  fields,
  values,
  onChange,
}: {
  fields: Field[];
  values: FormValues;
  onChange: (key: string, value: string | string[] | number) => void;
}) {
  const t = useTheme();
  return (
    <Column gap={20}>
      {fields.map((field) => (
        <Column key={field.key} gap={6}>
          {field.type === 'logo' ? (
            <LogoInput
              label={field.label}
              value={String(values[field.key] ?? '')}
              onChange={(value) => onChange(field.key, value)}
            />
          ) : field.type === 'multi' ? (
            <MultiChoice
              label={field.label}
              options={field.options ?? []}
              value={Array.isArray(values[field.key]) ? (values[field.key] as string[]) : []}
              onChange={(value) => onChange(field.key, value)}
              allowOther={field.allowOther}
            />
          ) : field.type === 'select' && field.allowOther ? (
            <CustomSelect
              field={field}
              value={String(values[field.key] ?? '')}
              onChange={(value) => onChange(field.key, value)}
            />
          ) : field.type === 'select' ? (
            <>
              <Copy size={13} bold>
                {field.label}
              </Copy>
              <Row wrap>
                {field.options?.map((option) => {
                  const value = values[field.key];
                  const selected = Array.isArray(value) ? value.includes(option) : value === option;
                  return (
                    <Pressable
                      key={option}
                      accessibilityRole="radio"
                      accessibilityLabel={`${field.label}: ${option}`}
                      accessibilityState={{ checked: selected }}
                      aria-checked={selected}
                      onPress={() => onChange(field.key, option)}
                      style={{
                        paddingVertical: 11,
                        paddingHorizontal: 13,
                        borderRadius: 11,
                        borderWidth: 1,
                        borderColor: selected ? t.accent : t.border,
                        backgroundColor: selected ? t.accentSoft : t.surface,
                        minHeight: 44,
                      }}
                    >
                      <Copy size={13} color={selected ? t.accent : t.muted}>
                        {selected ? '✓  ' : ''}
                        {option}
                      </Copy>
                    </Pressable>
                  );
                })}
              </Row>
            </>
          ) : (
            <Input
              label={field.label}
              value={String(values[field.key] ?? '')}
              onChange={(v) =>
                onChange(field.key, field.type === 'number' ? (v === '' ? '' : Number(v)) : v)
              }
              placeholder={field.hint}
              multiline={field.type === 'long'}
              secret={field.type === 'secret'}
              numeric={field.type === 'number'}
            />
          )}
          {!!field.hint && (field.type === 'select' || field.type === 'multi') && (
            <Copy size={12} muted>
              {field.hint}
            </Copy>
          )}
        </Column>
      ))}
    </Column>
  );
}
export function Sheet({
  title,
  children,
  onClose,
  showNotice = true,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  showNotice?: boolean;
}) {
  const t = useTheme();
  return (
    <Modal
      transparent
      visible
      animationType={useApp().reduceMotion ? 'none' : 'fade'}
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{
          flex: 1,
          backgroundColor: t.overlay,
          justifyContent: 'center',
          alignItems: 'center',
          padding: 16,
        }}
      >
        <Pressable
          style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
          onPress={onClose}
          accessibilityLabel="Close pop-up"
        />
        <View
          accessibilityViewIsModal
          style={{
            maxHeight: '94%',
            width: '100%',
            maxWidth: 640,
            backgroundColor: t.background,
            borderRadius: 24,
            overflow: 'hidden',
          }}
        >
          <Row between style={{ padding: 20, borderBottomWidth: 1, borderColor: t.border }}>
            <Button kind="quiet" compact label="Back" onPress={onClose}>
              ←
            </Button>
            <View style={{ flex: 1 }}>
              <Heading size={22}>{title}</Heading>
            </View>
          </Row>
          <ScrollView
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ padding: 24, gap: 20 }}
          >
            {showNotice && <Notice />}
            {children}
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );
}
export function FormSheet({
  title,
  fields,
  initial,
  onSave,
  onClose,
  submit = 'Save',
  intro,
}: {
  title: string;
  fields: Field[];
  initial: FormValues;
  onSave: (v: FormValues) => Promise<boolean>;
  onClose: () => void;
  submit?: string;
  intro?: string;
}) {
  const [values, setValues] = useState(initial);
  const [validation, setValidation] = useState('');
  const { busy, error } = useApp();
  const [attempted, setAttempted] = useState(false);
  const t = useTheme();
  async function save() {
    setValidation('');
    setAttempted(true);
    const missing = fields.find(
      (f) =>
        f.required &&
        (values[f.key] === undefined ||
          (typeof values[f.key] === 'string' && !(values[f.key] as string).trim()) ||
          (Array.isArray(values[f.key]) && (values[f.key] as string[]).length === 0)),
    );
    if (missing) {
      setValidation(`Please complete ${missing.label.toLowerCase()}.`);
      return;
    }
    try {
      if (await onSave(values)) onClose();
    } catch (e) {
      setValidation(e instanceof Error ? e.message : 'Please check your entries.');
    }
  }
  return (
    <Sheet title={title} onClose={onClose} showNotice={false}>
      {!!intro && <Copy muted>{intro}</Copy>}
      <FormFields
        fields={fields}
        values={values}
        onChange={(key, value) => {
          setValues((v) => ({ ...v, [key]: value }));
          setValidation('');
          setAttempted(false);
        }}
      />
      <Button feedback={false} disabled={busy} onPress={() => void save()}>
        {busy ? 'Saving…' : submit}
      </Button>
      {!!(validation || (attempted && error)) && (
        <View
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
          style={{ padding: 8, borderRadius: 8, borderWidth: 1, borderColor: t.danger }}
        >
          <Copy size={12} color={t.danger}>
            {validation || (error.startsWith('[') ? 'Check the fields and try again.' : error)}
          </Copy>
        </View>
      )}
    </Sheet>
  );
}
export function dateLabel(value: string) {
  return new Date(value.length === 10 ? value + 'T12:00:00' : value).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}
export function timeLabel(value: string) {
  return new Date(value).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function Disclosure({
  title,
  children,
  open = false,
}: {
  title: string;
  children: ReactNode;
  open?: boolean;
}) {
  const [expanded, setExpanded] = useState(open);
  const t = useTheme();
  return (
    <View style={{ borderBottomWidth: 1, borderColor: t.border, paddingVertical: 12 }}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={title}
        accessibilityState={{ expanded }}
        onPress={() => setExpanded(!expanded)}
        style={{ minHeight: 48, justifyContent: 'center' }}
      >
        <Row between>
          <Heading size={20}>{title}</Heading>
          <Copy muted>{expanded ? '−' : '+'}</Copy>
        </Row>
      </Pressable>
      {expanded && <Column style={{ paddingTop: 16, paddingBottom: 12 }}>{children}</Column>}
    </View>
  );
}
